/**
 * SoftModal — generic, dismissible modal chrome: portal, backdrop, "X" button,
 * ESC-to-close, backdrop-click-to-close, focus trap, and aria-hiding of the
 * rest of the app from assistive technology while open.
 *
 * Deliberately generic (no legal-specific content): a future generic
 * `announcements` system can reuse this chrome (spec "Out of Scope" note,
 * anti-M11). Content-specific concerns (checkboxes, submit, telemetry) live
 * in the consumer (see ReacceptanceModal), not here.
 *
 * Closing surfaces are configurable: the "X" always closes; backdrop-click and
 * ESC each default to closing but can be disabled per instance
 * (`dismissOnBackdrop` / `dismissOnEscape`). The legal consent modal disables
 * both so it is closable ONLY via the explicit "X" (user request 2026-07-04) —
 * a future generic reuse can keep the friendlier defaults. Whether the caller
 * treats a close as temporary or permanent is its own decision
 * (LegalConsentProvider treats it as temporary, per-view).
 */
import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

export interface SoftModalProps {
  /** id of the element used as aria-labelledby (rendered by children). */
  titleId: string;
  /** id of the element used as aria-describedby (rendered by children), if any. */
  descriptionId?: string;
  /** Fired on X click, and (unless disabled below) on ESC and backdrop click. */
  onClose: () => void;
  children: React.ReactNode;
  closeLabel?: string;
  /** Whether a backdrop (overlay) click closes the modal. Default `true`. */
  dismissOnBackdrop?: boolean;
  /** Whether pressing Escape closes the modal. Default `true`. */
  dismissOnEscape?: boolean;
  /**
   * Element to receive focus on mount. Defaults to the "X" close button (a11y:
   * first, predictable tab stop). The legal consent modal points this at its
   * accept checkbox so the primary action is focused on open (user request
   * 2026-07-04). If the ref is unset when the modal mounts, focus falls back to
   * the close button.
   */
  initialFocusRef?: React.RefObject<HTMLElement | null>;
  "data-testid"?: string;
}

/** Focus-return target on close — never leave focus on <body> (anti-pattern). */
const MAIN_CONTENT_ID = "main-content";

export const SoftModal: React.FC<SoftModalProps> = ({
  titleId,
  descriptionId,
  onClose,
  children,
  closeLabel = "Zamknij",
  dismissOnBackdrop = true,
  dismissOnEscape = true,
  initialFocusRef,
  "data-testid": dataTestId = "soft-modal",
}) => {
  const modalRef = useRef<HTMLDivElement | null>(null);
  const portalRootRef = useRef<HTMLDivElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  // Auto-focus on mount: the caller-provided target (e.g. the accept checkbox)
  // if any, otherwise the close button (a11y: first, predictable tab stop).
  useEffect(() => {
    (initialFocusRef?.current ?? closeButtonRef.current)?.focus();
  }, [initialFocusRef]);

  // Scroll lock — prevent background scroll while the modal is open.
  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, []);

  // a11y: hide everything else in the app from the screen-reader virtual
  // cursor while the modal is open (Sidebar/TopBar chrome, CookieBanner, GTM
  // tracker, etc. — all mounted as siblings in document.body). Only elements
  // we set are restored on unmount (something already aria-hidden by someone
  // else is left alone).
  useEffect(() => {
    const hidden: Element[] = [];
    Array.from(document.body.children).forEach((el) => {
      if (el === portalRootRef.current) return;
      if (el.getAttribute("aria-hidden") === "true") return;
      el.setAttribute("aria-hidden", "true");
      hidden.push(el);
    });
    return () => {
      hidden.forEach((el) => el.removeAttribute("aria-hidden"));
    };
  }, []);

  // Focus-return: once this unmounts, move focus to the main content
  // landmark so it never lands on <body> (the browser's default once the
  // previously-focused node is removed from the DOM).
  useEffect(() => {
    return () => {
      document.getElementById(MAIN_CONTENT_ID)?.focus();
    };
  }, []);

  // ESC closes; focus trap keeps Tab/Shift+Tab cycling within the dialog.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (dismissOnEscape) {
          e.preventDefault();
          onClose();
        }
        return;
      }
      if (e.key !== "Tab") return;
      const modalNode = modalRef.current;
      if (!modalNode) return;
      const focusable = modalNode.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [onClose, dismissOnEscape]);

  return createPortal(
    <div
      ref={portalRootRef}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[100]"
      onClick={(e) => {
        // Backdrop click only (never clicks bubbling up from the dialog) — and
        // only when enabled. The legal consent modal disables this so the
        // overlay is inert and the "X" is the sole close affordance.
        if (dismissOnBackdrop && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        className="relative bg-white rounded-lg shadow-xl max-w-lg w-full min-w-0 overflow-hidden flex flex-col max-h-[90vh]"
        data-testid={dataTestId}
      >
        <button
          ref={closeButtonRef}
          type="button"
          onClick={onClose}
          aria-label={closeLabel}
          className="absolute top-3 right-3 p-1.5 rounded-md text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          data-testid="soft-modal-close"
        >
          <svg
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
        {children}
      </div>
    </div>,
    document.body,
  );
};

export default SoftModal;
